// DataETL.js
import React, { useState, useRef } from 'react';
import {
  Container, Typography, Paper, Button, Box,
  FormControlLabel, Checkbox, FormControl, Select,
  InputLabel, MenuItem, ListItemText
} from '@mui/material';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

const DataETL = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [transformedBlob, setTransformedBlob] = useState(null);
  const [ignoreEmptyValues, setIgnoreEmptyValues] = useState(false);
  const [ignoreBlankFieldNames, setIgnoreBlankFieldNames] = useState(false);
  const [notesCsvFile, setNotesCsvFile] = useState(null);
  const [transformedNotesBlob, setTransformedNotesBlob] = useState(null);

  //for filtering Forms
  const [transformedRows, setTransformedRows] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedFieldNames, setSelectedFieldNames] = useState([]);
  const availableNames = [...new Set(transformedRows.map(r => r.Name))];
  const availableSections = [...new Set(
    transformedRows.filter(r => selectedNames.includes(r.Name)).map(r => r.Section)
  )];
  const availableFieldNames = [...new Set(
    transformedRows.filter(
      r => selectedNames.includes(r.Name) && selectedSections.includes(r.Section)
    ).map(r => r.FieldName)
  )];


  const [formDateRange, setFormDateRange] = useState({ min: '', max: '' });
  const [formFilterDates, setFormFilterDates] = useState({ from: '', to: '' });
  const [notesDateRange, setNotesDateRange] = useState({ min: '', max: '' });
  const [notesFilterDates, setNotesFilterDates] = useState({ from: '', to: '' });


  // Delay-scroll the dropdown menu to the top after rendering
  const scrollMenuToTop = () => {
    const menuLists = document.querySelectorAll('ul.MuiMenu-list');

    menuLists.forEach((menu, idx) => {
      const scrollContainer = menu.parentElement; // Usually the Paper div
      if (scrollContainer && scrollContainer.scrollTop > 0) {
        console.log(`Scrolling dropdown ${idx} to top`);
        scrollContainer.scrollTo({ top: 0 });
      } else {
        console.log(`Dropdown ${idx} already at top or no scrollable container`);
      }
    });
  };


  // Reusable multi-select handler
  const handleMultiSelect = ({
    value,
    allValue,
    selected,
    available,
    setSelected,
    onReset = () => { },
  }) => {
    if (value.includes(allValue)) {
      const allSelected = selected.length === available.length;
      setSelected(allSelected ? [] : [...available]);
      if (!allSelected) onReset();
    } else {
      setSelected(value);
    }
  };

  // Shared menu props
  const getMenuProps = () => ({
    PaperProps: {
      sx: {
        maxHeight: 400,
        minWidth: 300,
        width: 'auto',
      },
    },
  });



  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
    setTransformedBlob(null);

    Papa.parse(file, {
      header: true,
      complete: (results) => {
        const dates = results.data
          .map((r) => new Date(r['Created']))
          .filter((d) => !isNaN(d));
        if (dates.length > 0) {
          const minDate = new Date(Math.min(...dates));
          const maxDate = new Date(Math.max(...dates));
          const format = (d) => d.toISOString().substring(0, 10);
          setFormDateRange({ min: format(minDate), max: format(maxDate) });
          setFormFilterDates({ from: format(minDate), to: format(maxDate) });
        }
      },
    });
  };

  /*
    const handleNotesFileUpload = (e) => {
      setNotesCsvFile(e.target.files[0]);
      setTransformedNotesBlob(null);
    };
  */

  const handleNotesFileUpload = (e) => {
    const file = e.target.files[0];
    setNotesCsvFile(e.target.files[0]);
    setTransformedNotesBlob(null);

    Papa.parse(file, {
      header: true,
      complete: (results) => {
        const dates = results.data
          .map((r) => new Date(r['Created']))
          .filter((d) => !isNaN(d));
        if (dates.length > 0) {
          const minDate = new Date(Math.min(...dates));
          const maxDate = new Date(Math.max(...dates));
          const format = (d) => d.toISOString().substring(0, 10);
          setNotesDateRange({ min: format(minDate), max: format(maxDate) });
          setNotesFilterDates({ from: format(minDate), to: format(maxDate) });
        }
      },
    });
  };


  const transformData = () => {
    if (!csvFile) return;

    Papa.parse(csvFile, {
      header: true,
      complete: (results) => {
        const transformed = [];

        results.data.forEach((row) => {
          const common = {
            FullName: row['Full Name'],
            FormReference: row['Form Reference'],
            ClientNumber: row['ClientNumber'],
            Name: row['Name'],
            Created: row['Created'],
          };

          const createdDate = new Date(row['Created']);
          const from = new Date(formFilterDates.from);
          const to = new Date(formFilterDates.to);
          if (isNaN(createdDate) || createdDate < from || createdDate > to) {
            return; // Skip this row
          }

          const forms = row['Forms'] || '';
          const lines = forms.split(/\r?\n/);
          let currentSection = '';

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();

            if (line.startsWith('Section')) {
              currentSection = line.slice(8).trim();
            } else if (line.startsWith('Field')) {
              let fieldName = 'Field';
              let fieldValue = '';

              const afterField = line.slice(5).trim();
              if (afterField) {
                const cleaned = afterField.replace(/^:\s*/, '');
                fieldName = cleaned.trim() || '';
              }

              const nextLine = lines[i + 1]?.trim();
              if (nextLine && !nextLine.startsWith('Field') && !nextLine.startsWith('Section')) {
                fieldValue = nextLine;
                i++;
              }

              // Apply optional ignore rules
              if (ignoreBlankFieldNames && !fieldName) continue;
              if (ignoreEmptyValues && !fieldValue) continue;

              transformed.push({
                ...common,
                Section: currentSection,
                FieldName: fieldName || 'Field',
                FieldValue: fieldValue,
              });
            }
          }

        });

        // For filtering
        setTransformedRows(transformed); // right before generating the blob

        const csv = Papa.unparse(transformed);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        setTransformedBlob(blob);
      },
    });
  };

  const transformNotesData = () => {
    if (!notesCsvFile) return;

    Papa.parse(notesCsvFile, {
      header: true,
      complete: (results) => {
        const transformed = [];

        results.data.forEach((row) => {
          const common = {
            FullName: row['Full Name'],
            NoteReference: row['Note Reference'],
            ClientNumber: row['ClientNumber'],
            Name: row['Name'],
            Created: row['Created'],
          };

          const createdDate = new Date(row['Created']);
          const from = new Date(notesFilterDates.from);
          const to = new Date(notesFilterDates.to);
          if (isNaN(createdDate) || createdDate < from || createdDate > to) {
            return; // Skip this row
          }

          const notes = row['Notes'] || '';
          const lines = notes.split(/\r?\n/);
          let currentSection = '';

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();

            if (line.startsWith('Section')) {
              currentSection = line.slice(8).trim();
            } else if (line.startsWith('Field')) {
              let fieldName = 'Field';
              let fieldValue = '';

              const afterField = line.slice(5).trim();
              if (afterField) {
                const cleaned = afterField.replace(/^:\s*/, '');
                fieldName = cleaned.trim() || '';
              }

              const nextLine = lines[i + 1]?.trim();
              if (nextLine && !nextLine.startsWith('Field') && !nextLine.startsWith('Section')) {
                fieldValue = nextLine;
                i++;
              }

              if (ignoreBlankFieldNames && !fieldName) continue;
              if (ignoreEmptyValues && !fieldValue) continue;

              transformed.push({
                ...common,
                Section: currentSection,
                FieldName: fieldName || 'Field',
                FieldValue: fieldValue,
              });
            }
          }
        });

        const csv = Papa.unparse(transformed);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        setTransformedNotesBlob(blob);
      },
    });
  };


  const downloadCsv = () => {
    if (transformedBlob) {
      saveAs(transformedBlob, 'Transformed_Client_Forms.csv');
    }
  };

  const downloadFilteredCsv = () => {
    const from = new Date(formFilterDates.from);
    const to = new Date(formFilterDates.to);

    const filtered = transformedRows.filter((row) => {
      const createdDate = new Date(row.Created);
      return (
        selectedNames.includes(row.Name) &&
        selectedSections.includes(row.Section) &&
        selectedFieldNames.includes(row.FieldName) &&
        createdDate >= from && createdDate <= to
      );
    });

    const csv = Papa.unparse(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'Filtered_Transformed_Client_Forms.csv');
  };


  return (
    <Container maxWidth="lg" style={{ marginTop: '2rem' }}>
      <Paper elevation={3} style={{ padding: 20 }}>
        <Typography variant="body1" gutterBottom>
          These buttons will create a new .CSV file that pivots all the section, field, and field values into seperate rows by client so they can be searched.
        </Typography>

        <Typography variant="h5" gutterBottom>
          Client Forms Processing
        </Typography>
        <Box mt={2}>
          <input
            type="file"
            accept=".csv"
            id="upload-client-forms"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <label htmlFor="upload-client-forms">
            <Button variant="contained" component="span">
              Upload Client Forms CSV
            </Button>
          </label>

          {csvFile && (
            <Typography variant="body2" sx={{ mt: 1, color: 'green' }}>
              File uploaded: {csvFile.name}
            </Typography>
          )}
        </Box>

        {csvFile && (
          <>
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ignoreEmptyValues}
                    onChange={(e) => setIgnoreEmptyValues(e.target.checked)}
                  />
                }
                label="Ignore Empty Field Values"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ignoreBlankFieldNames}
                    onChange={(e) => setIgnoreBlankFieldNames(e.target.checked)}
                  />
                }
                label="Ignore Blank Field Names"
              />

            </Box>
            <Box mt={2}>
              <Typography variant="subtitle1">Filter by Created Date</Typography>
              <Typography variant="subtitle1">Original range is from <strong>{formDateRange.min}</strong> to <strong>{formDateRange.max}</strong></Typography>
              <Box display="flex" gap={2} mt={1}>
                <input
                  type="date"
                  value={formFilterDates.from}
                  onChange={(e) =>
                    setFormFilterDates({ ...formFilterDates, from: e.target.value })
                  }
                />
                <input
                  type="date"
                  value={formFilterDates.to}
                  onChange={(e) =>
                    setFormFilterDates({ ...formFilterDates, to: e.target.value })
                  }
                />
              </Box>
            </Box>
            <Box mt={2}>
              <Button variant="contained" onClick={transformData}>
                Transform CLIENT FORMS Data
              </Button>
            </Box>
          </>
        )}

        {transformedRows.length > 0 && (
          <Box mt={4}>
            <Typography variant="h6" gutterBottom>
              Filter Transformed File
            </Typography>

            <Box mt={2} display="flex" gap={2} flexWrap="wrap">
              {/* Names Dropdown */}
              <FormControl sx={{ width: 250 }} size="small">
                <InputLabel sx={{ '&.MuiInputLabel-shrink': { top: -6 } }}>Names</InputLabel>
                <Select
                  multiple
                  value={selectedNames}
                  onChange={(e) =>
                    handleMultiSelect({
                      value: e.target.value,
                      allValue: '__ALL__',
                      selected: selectedNames,
                      available: availableNames,
                      setSelected: setSelectedNames,
                      onReset: () => {
                        setSelectedSections([]);
                        setSelectedFieldNames([]);
                      },
                    })
                  }
                  onOpen={() => {
                    if (selectedNames.length === availableNames.length) {
                      setTimeout(() => {
                        scrollMenuToTop();
                      }, 50);
                    }
                  }}
                  renderValue={(selected) => selected.map((v) => v || '(No Name)').join(', ')}
                  MenuProps={getMenuProps()}
                >
                  <MenuItem value="__ALL__">
                    <Checkbox checked={selectedNames.length === availableNames.length} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {availableNames.map((name) => (
                    <MenuItem key={name || '__NO_NAME__'} value={name}>
                      <Checkbox checked={selectedNames.includes(name)} />
                      <ListItemText primary={name || '(No Name)'} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Sections Dropdown */}
              <FormControl sx={{ width: 250 }} size="small" disabled={selectedNames.length === 0}>
                <InputLabel sx={{ '&.MuiInputLabel-shrink': { top: -6 } }}>Sections</InputLabel>
                <Select
                  multiple
                  value={selectedSections}
                  onChange={(e) =>
                    handleMultiSelect({
                      value: e.target.value,
                      allValue: '__ALL__',
                      selected: selectedSections,
                      available: availableSections,
                      setSelected: setSelectedSections,
                      onReset: () => setSelectedFieldNames([]),
                    })
                  }
                  onOpen={() => {
                    if (selectedNames.length === availableNames.length) {
                      setTimeout(() => {
                        scrollMenuToTop();
                      }, 50);
                    }
                  }}
                  renderValue={(selected) => selected.map((v) => v || '(No Section Name)').join(', ')}
                  MenuProps={getMenuProps()}
                >
                  <MenuItem value="__ALL__">
                    <Checkbox checked={selectedSections.length === availableSections.length} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {availableSections.map((section) => (
                    <MenuItem key={section || '__NO_SECTION__'} value={section}>
                      <Checkbox checked={selectedSections.includes(section)} />
                      <ListItemText primary={section || '(No Section Name)'} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Field Names Dropdown */}
              <FormControl sx={{ width: 250 }} size="small" disabled={selectedSections.length === 0}>
                <InputLabel sx={{ '&.MuiInputLabel-shrink': { top: -6 } }}>Field Names</InputLabel>
                <Select
                  multiple
                  value={selectedFieldNames}
                  onChange={(e) =>
                    handleMultiSelect({
                      value: e.target.value,
                      allValue: '__ALL__',
                      selected: selectedFieldNames,
                      available: availableFieldNames,
                      setSelected: setSelectedFieldNames,
                    })
                  }
                  onOpen={() => {
                    if (selectedNames.length === availableNames.length) {
                      setTimeout(() => {
                        scrollMenuToTop();
                      }, 50);
                    }
                  }}
                  renderValue={(selected) => selected.map((v) => v || '(No Field Name)').join(', ')}
                  MenuProps={getMenuProps()}
                >
                  <MenuItem value="__ALL__">
                    <Checkbox checked={selectedFieldNames.length === availableFieldNames.length} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {availableFieldNames.map((fieldName) => (
                    <MenuItem key={fieldName || '__NO_FIELD__'} value={fieldName}>
                      <Checkbox checked={selectedFieldNames.includes(fieldName)} />
                      <ListItemText primary={fieldName || '(No Field Name)'} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box mt={2}>
              <Button variant="outlined" onClick={downloadFilteredCsv}>
                Download Filtered Transformed Client Forms
              </Button>
            </Box>
          </Box>
        )}

        {transformedBlob && (
          <Box mt={2}>
            <Button variant="outlined" onClick={downloadCsv}>
              Download Full Transformed Client Forms
            </Button>
          </Box>
        )}

        <Box mt={4}>
          <Typography variant="h5" gutterBottom>
            Client Notes Processing
          </Typography>

          <Box mt={2}>
            <input
              type="file"
              accept=".csv"
              id="upload-client-notes"
              style={{ display: 'none' }}
              onChange={handleNotesFileUpload}
            />
            <label htmlFor="upload-client-notes">
              <Button variant="contained" component="span">
                Upload Client Notes CSV
              </Button>
            </label>

            {notesCsvFile && (
              <Typography variant="body2" sx={{ mt: 1, color: 'green' }}>
                File uploaded: {notesCsvFile.name}
              </Typography>
            )}
          </Box>

          {notesCsvFile && (
            <>
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ignoreEmptyValues}
                      onChange={(e) => setIgnoreEmptyValues(e.target.checked)}
                    />
                  }
                  label="Ignore Empty Field Values"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ignoreBlankFieldNames}
                      onChange={(e) => setIgnoreBlankFieldNames(e.target.checked)}
                    />
                  }
                  label="Ignore Blank Field Names"
                />
              </Box>
              <Box mt={2}>
                <Typography variant="subtitle1">Filter by Created Date</Typography>
                <Typography variant="subtitle1">Original range is from <strong>{notesDateRange.min}</strong> to <strong>{notesDateRange.max}</strong></Typography>
                <Box display="flex" gap={2} mt={1}>
                  <input
                    type="date"
                    value={notesFilterDates.from}
                    onChange={(e) =>
                      setNotesFilterDates({ ...notesFilterDates, from: e.target.value })
                    }
                  />
                  <input
                    type="date"
                    value={notesFilterDates.to}
                    onChange={(e) =>
                      setNotesFilterDates({ ...notesFilterDates, to: e.target.value })
                    }
                  />
                </Box>
              </Box>
              <Box mt={2}>
                <Button variant="contained" onClick={transformNotesData}>
                  Transform Notes Data
                </Button>
              </Box>
            </>
          )}

          {transformedNotesBlob && (
            <Box mt={2}>
              <Button variant="outlined" onClick={() => saveAs(transformedNotesBlob, 'Transformed_Client_Notes.csv')}>
                Download Transformed Notes CSV
              </Button>
            </Box>
          )}
        </Box>

      </Paper>
    </Container>
  );
};

export default DataETL;
